import React from "react";
import { TextField, useTheme } from "cayo.ui";
import { PrimaryButton, Spinner, MessageBar, MessageBarType } from "@fluentui/react";
import styled from "styled-components";
import { useIntl, FormattedMessage } from "react-intl";
import { communicationChannelsWizardMessages } from "../../shared/messages";

type AccessEntraProps = {
  accountName: string;
  grantState: string;
  error: string;
  grantIsActive: boolean;
  processing: boolean;
  accountMissMatch: boolean;
  handleChange: (key: string, value: string) => void;
  onGrant: () => void;
};

export const AccessEntra = ({
  accountName,
  grantIsActive,
  grantState,
  processing,
  error,
  accountMissMatch,
  handleChange,
  onGrant,
}: AccessEntraProps) => {
  const intl = useIntl();
  const { cayoTheme } = useTheme();

  return (
    <div>
      <MessageBar
        messageBarType={MessageBarType.info}
        truncated={true}
        styles={{
          root: {
            margin: "10px 0 10px 0",
            background: cayoTheme.wizard.colors.active,
          },
        }}
      >
        <FormattedMessage
          id={communicationChannelsWizardMessages.accessEntraText.id}
          defaultMessage={communicationChannelsWizardMessages.accessEntraText.defaultMessage}
          values={{
            link: (
              <a
                href={
                  "https://learn.microsoft.com/en-us/entra/identity-platform/application-consent-experience"
                }
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage
                  id={communicationChannelsWizardMessages.LearnMoreAboutAdmin.id}
                  defaultMessage={
                    communicationChannelsWizardMessages.LearnMoreAboutAdmin.defaultMessage
                  }
                />
              </a>
            ),
          }}
        />
      </MessageBar>

      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          truncated={true}
          styles={{
            root: {
              margin: "10px 0 10px 0",
            },
          }}
        >
          {error}
        </MessageBar>
      )}

      {accountMissMatch && (
        <MessageBar
          messageBarType={MessageBarType.error}
          truncated={true}
          styles={{
            root: {
              margin: "10px 0 10px 0",
            },
          }}
        >
          {intl.formatMessage(communicationChannelsWizardMessages.adminAccountMissMatch)}
        </MessageBar>
      )}

      <TextField
        label={intl.formatMessage(communicationChannelsWizardMessages.accessEntraField)}
        required
        value={accountName}
        setValue={(value) => handleChange("name", value)}
      />

      <Wrapper>
        <GrantState className={grantState}>
          <span className="consent">Consent:</span>
          {processing ? <Spinner label={""} labelPosition="right" /> : `${grantState}`}
        </GrantState>
        <PrimaryButton
          text={intl.formatMessage(communicationChannelsWizardMessages.grant)}
          onClick={onGrant}
          disabled={!grantIsActive}
          styles={{ root: { marginTop: 10 } }}
        />
      </Wrapper>
    </div>
  );
};

const Wrapper = styled.div`
    display: flex; 
    gap 15px;
    align-items: center;
`;

const GrantState = styled.span`
  font-weight: 500;
  display: flex; 
  gap 10px;
  align-items: center;

  .consent {
    color: rgb(50, 49, 48) !important;
  }

  &.Granted {
    color: ${(props) => props.theme.cayoTheme.brandColors.success};
  }

  &.Invalid {
    color: ${(props) => props.theme.cayoTheme.brandColors.error};
  }
`;
